import { supabase } from '@/integrations/supabase/client';

export type FollowRequestStatus = 'none' | 'pending' | 'accepted' | 'rejected';

export interface FollowRequestResult {
  success: boolean;
  action?: 'request_sent' | 'request_canceled' | 'request_accepted' | 'request_rejected';
  error?: any;
}

export class FollowRequestService {
  /**
   * Send a follow request to a private profile
   */
  static async sendFollowRequest(targetUserId: string): Promise<FollowRequestResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error } = await supabase
        .from('follow_requests')
        .insert({
          requester_id: session.user.id,
          requested_id: targetUserId,
          status: 'pending'
        });

      if (error) {
        console.error('Error sending follow request:', error);
        return { success: false, error };
      }

      return { success: true, action: 'request_sent' };
    } catch (error) {
      console.error('Unexpected error sending follow request:', error);
      return { success: false, error };
    }
  }

  /**
   * Cancel a pending follow request
   */
  static async cancelFollowRequest(targetUserId: string): Promise<FollowRequestResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error } = await supabase
        .from('follow_requests')
        .delete()
        .eq('requester_id', session.user.id)
        .eq('requested_id', targetUserId);

      if (error) {
        console.error('Error canceling follow request:', error);
        return { success: false, error };
      }

      return { success: true, action: 'request_canceled' };
    } catch (error) {
      console.error('Unexpected error canceling follow request:', error);
      return { success: false, error };
    }
  }

  /**
   * Accept a follow request (for profile owners)
   */
  static async acceptFollowRequest(requesterId: string): Promise<FollowRequestResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Start transaction by updating request status
      const { error: updateError } = await supabase
        .from('follow_requests')
        .update({ status: 'accepted' })
        .eq('requester_id', requesterId)
        .eq('requested_id', session.user.id);

      if (updateError) {
        console.error('Error updating follow request:', updateError);
        return { success: false, error: updateError };
      }

      // Create the actual follow relationship
      const { error: followError } = await supabase
        .from('follows')
        .insert({
          follower_id: requesterId,
          following_id: session.user.id,
        });

      if (followError) {
        console.error('Error creating follow relationship:', followError);
        return { success: false, error: followError };
      }

      // Delete the request after successful follow
      await supabase
        .from('follow_requests')
        .delete()
        .eq('requester_id', requesterId)
        .eq('requested_id', session.user.id);

      return { success: true, action: 'request_accepted' };
    } catch (error) {
      console.error('Unexpected error accepting follow request:', error);
      return { success: false, error };
    }
  }

  /**
   * Reject a follow request
   */
  static async rejectFollowRequest(requesterId: string): Promise<FollowRequestResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error } = await supabase
        .from('follow_requests')
        .update({ status: 'rejected' })
        .eq('requester_id', requesterId)
        .eq('requested_id', session.user.id);

      if (error) {
        console.error('Error rejecting follow request:', error);
        return { success: false, error };
      }

      return { success: true, action: 'request_rejected' };
    } catch (error) {
      console.error('Unexpected error rejecting follow request:', error);
      return { success: false, error };
    }
  }

  /**
   * Get follow request status between two users
   */
  static async getFollowRequestStatus(targetUserId: string): Promise<FollowRequestStatus> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return 'none';

      const { data, error } = await supabase
        .from('follow_requests')
        .select('status')
        .eq('requester_id', session.user.id)
        .eq('requested_id', targetUserId)
        .maybeSingle();

      if (error) {
        console.error('Error getting follow request status:', error);
        return 'none';
      }

      return (data?.status as FollowRequestStatus) || 'none';
    } catch (error) {
      console.error('Unexpected error getting follow request status:', error);
      return 'none';
    }
  }

  /**
   * Get pending follow requests for current user (as the requested user)
   */
  static async getPendingRequests() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];

      const { data, error } = await supabase
        .from('follow_requests')
        .select(`
          *,
          profiles:requester_id (
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('requested_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting pending requests:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Unexpected error getting pending requests:', error);
      return [];
    }
  }
}